import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

export interface TeeVideoPlayerProps {
  src: string;
  poster?: string;
  className?: string;
  errorLabel?: string;
  /** Autoplay requires muting (browser policy) — the video starts muted and playing; the user can unmute via controls. */
  autoPlay?: boolean;
}

/**
 * Single point of integration for HLS video playback across box-os.
 * Currently backed by hls.js — if the underlying player ever needs to
 * change (e.g. to Gumlet's hosted iframe embed), this is the only file
 * that needs to change; every caller keeps the same src/poster contract.
 *
 * Prefers hls.js (MediaSource Extensions) whenever supported — this is
 * hls.js's own recommended check order. Some Chromium builds report
 * canPlayType('application/vnd.apple.mpegurl') as truthy despite having
 * no real HLS demuxer, which silently fails with
 * MEDIA_ERR_SRC_NOT_SUPPORTED if checked first. Native <video src> is the
 * correct choice only for browsers without MSE (legitimately just
 * iOS/Safari's older engines).
 */
export function TeeVideoPlayer({
  src,
  poster,
  className,
  errorLabel,
  autoPlay = false,
}: TeeVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  useEffect(() => {
    // `active` guards against React StrictMode's dev-only double-invoke
    // (mount -> cleanup -> mount): hls.destroy() clears video.src and
    // calls video.load() as part of teardown, which can fire a spurious
    // native "error" event that the about-to-be-removed listener would
    // otherwise still catch and misreport as a real playback failure.
    let active = true;
    setErrorDetail(null);
    const video = videoRef.current;
    if (!video) return;

    const onNativeError = () => {
      if (!active) return;
      const mediaError = video.error;
      setErrorDetail(
        mediaError ? `native video error (code ${mediaError.code})` : 'native video error'
      );
    };
    video.addEventListener('error', onNativeError);

    const play = () => {
      // Autoplay can still be rejected by browser policy even when muted
      // (rare) — swallow the promise rejection so it doesn't surface as an
      // unhandled rejection; the user can always press play manually.
      video.play().catch(() => {});
    };

    let hls: Hls | undefined;
    if (Hls.isSupported()) {
      hls = new Hls();
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!active) return;
        // eslint-disable-next-line no-console
        console.error('[TeeVideoPlayer] hls.js error', data);
        if (data.fatal) {
          setErrorDetail(`${data.type}: ${data.details}`);
        }
      });
      if (autoPlay) {
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (active) play();
        });
      }
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      if (autoPlay) play();
    } else {
      setErrorDetail('HLS playback is not supported in this browser');
    }

    return () => {
      active = false;
      video.removeEventListener('error', onNativeError);
      hls?.destroy();
    };
  }, [src, autoPlay]);

  return (
    <div className="flex w-full flex-col gap-2">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        controls
        poster={poster}
        className={className}
        autoPlay={autoPlay}
        muted={autoPlay}
        playsInline
      />
      {errorDetail && (
        <p className="text-body-xs text-[var(--text-badge-red)]">
          {errorLabel ?? "Couldn't play this video"} ({errorDetail})
        </p>
      )}
    </div>
  );
}

export default TeeVideoPlayer;
